import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { devLog } from "../lib/devLog";
import { useAuth } from "../contexts/AuthContext";
import { usePolling } from "../lib/usePolling";
import { Navbar } from "../components/Navbar";
import { Plus, FolderOpen, ArrowRight, X } from "@phosphor-icons/react";

export default function Dashboard() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch (e) {
      devLog.error("[Dashboard] load failed", e);
      /* keep last good list on transient failures */
    }
    if (!silent) setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live updates — silent refresh keeps the list current without spinner flicker
  usePolling(() => load(true), 8000);

  return (
    <div>
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="label-mono mb-2">Workspace</p>
            <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter leading-none">
              Mission control.
            </h1>
            <p className="text-[color:var(--text-secondary)] mt-3">
              Welcome back, <span className="text-white font-semibold">{user?.name}</span>. Choose a project or spin up a new operation.
            </p>
          </div>
          {user?.role === "admin" && (
            <button data-testid="new-project-button" onClick={() => setShowModal(true)} className="btn-primary">
              <Plus size={16} weight="bold" /> New Project
            </button>
          )}
        </div>

        {loading ? (
          <p className="text-[color:var(--text-tertiary)]">Loading projects...</p>
        ) : projects.length === 0 ? (
          <EmptyState canCreate={user?.role === "admin"} onCreate={() => setShowModal(true)} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="projects-grid">
            {projects.map((p) => (
              <ProjectCard key={p.id} project={p} isAdmin={p.admin_id === user?.id} />
            ))}
          </div>
        )}
      </main>
      {showModal && (
        <CreateProjectModal
          onClose={() => setShowModal(false)}
          onCreated={() => { setShowModal(false); load(); }}
        />
      )}
    </div>
  );
}

function ProjectCard({ project, isAdmin }) {
  return (
    <Link
      data-testid={`project-card-${project.id}`}
      to={`/projects/${project.id}`}
      className="surface p-6 hover:bg-[color:var(--surface-hover)] transition-all duration-200 hover:-translate-y-0.5 group block rounded-md"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="w-10 h-10 rounded-md bg-[color:var(--surface-2)] border border-[color:var(--border)] flex items-center justify-center">
          <FolderOpen size={20} weight="duotone" className="text-[#007AFF]" />
        </div>
        {isAdmin && <span className="role-badge role-admin">Admin</span>}
      </div>
      <h3 className="font-heading text-xl font-bold tracking-tight mb-1">{project.name}</h3>
      <p className="text-sm text-[color:var(--text-secondary)] line-clamp-2 min-h-[40px]">
        {project.description || "No description provided."}
      </p>
      <div className="mt-4 pt-4 border-t border-[color:var(--border)] flex items-center justify-between">
        <span className="label-mono">{project.member_ids?.length || 0} members</span>
        <ArrowRight size={16} className="text-[#007AFF] group-hover:translate-x-1 transition" />
      </div>
    </Link>
  );
}

function EmptyState({ canCreate, onCreate }) {
  return (
    <div className="surface p-16 rounded-md text-center">
      <FolderOpen size={48} weight="duotone" className="text-[color:var(--text-tertiary)] mx-auto mb-4" />
      <h3 className="font-heading text-xl font-bold mb-2">No projects yet</h3>
      <p className="text-[color:var(--text-secondary)] mb-6">
        {canCreate
          ? "Create your first project to start coordinating tasks."
          : "Ask a project admin to invite you to their workspace."}
      </p>
      {canCreate && (
        <button data-testid="empty-create-project-button" onClick={onCreate} className="btn-primary">
          <Plus size={16} weight="bold" /> Create project
        </button>
      )}
    </div>
  );
}

function CreateProjectModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/projects", { name, description });
      onCreated();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    }
    setSaving(false);
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-6 border-b border-[color:var(--border)]">
          <h2 className="font-heading text-xl font-bold tracking-tight">New project</h2>
          <button onClick={onClose} className="text-[color:var(--text-secondary)] hover:text-white" data-testid="close-create-project">
            <X size={20} />
          </button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="label-mono block mb-2">Project name</label>
            <input data-testid="project-name-input" required className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Q1 Campaign Launch" />
          </div>
          <div>
            <label className="label-mono block mb-2">Description</label>
            <textarea data-testid="project-description-input" className="input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="What's this project about?" />
          </div>
          {error && <div className="text-sm text-red-400">{error}</div>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-ghost flex-1 justify-center">Cancel</button>
            <button data-testid="submit-create-project-button" disabled={saving} className="btn-primary flex-1 justify-center">
              {saving ? "Creating..." : "Create project"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
